import React from "react";

export type TransactionTone = "paid" | "failed" | "pending";

const PILL: Record<TransactionTone, { label: string; classes: string }> = {
  paid: { label: "PAID", classes: "bg-brand-soft text-brand-dark" },
  failed: { label: "NOT PAID", classes: "bg-[#FDECEC] text-[#D14343]" },
  pending: { label: "PENDING", classes: "bg-[#FFF6E5] text-[#9A6700]" },
};

export interface TransactionRow {
  label: string;
  /** Left off entirely where the answer is not known — see below. */
  value?: string | null;
}

interface TransactionDetailsProps {
  rows: TransactionRow[];
  tone: TransactionTone;
}

/**
 * The receipt: what was bought, for how much, by what, and when it renews.
 *
 * A row with no value is dropped rather than drawn with a dash. On a receipt a
 * dash is not "we do not know yet" — it reads as a field that is genuinely
 * empty, which for an invoice number or a payment method is a different and
 * more alarming claim. What the screen cannot say, it does not say. That is
 * also what lets one card serve a payment taken in test mode, where there is
 * no invoice number to print, and a real one, where there is.
 */
const TransactionDetails: React.FC<TransactionDetailsProps> = ({
  rows,
  tone,
}) => {
  const known = rows.filter((row) => !!row.value);
  const pill = PILL[tone];

  return (
    <section
      aria-labelledby="transaction-details-heading"
      className="mt-8 rounded-md border border-line bg-white text-left"
    >
      <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-4 3xl:px-6">
        <h3
          id="transaction-details-heading"
          className="text-[15px] 3xl:text-[16px] font-medium text-ink"
        >
          Transaction details
        </h3>
        <span
          className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] 3xl:text-[12px] font-semibold tracking-wide ${pill.classes}`}
        >
          {pill.label}
        </span>
      </div>

      <dl className="flex flex-col gap-3 px-5 py-4 3xl:px-6 3xl:py-5">
        {known.map((row) => (
          <div
            key={row.label}
            className="flex items-baseline justify-between gap-4"
          >
            <dt className="text-[13px] 3xl:text-[14px] text-ink-muted">
              {row.label}
            </dt>
            {/* Tabular figures so amounts, dates and reference numbers line up
                down the right edge rather than wandering by a pixel each. */}
            <dd className="tabular-nums text-right text-[13px] 3xl:text-[14px] font-medium text-ink">
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export default TransactionDetails;
